import React from 'react';
import { faAddressCard } from '@fortawesome/free-solid-svg-icons';
import { Row, Col, Input, FormGroup } from 'reactstrap';
import { debounce } from 'lodash-es';
import AddressInput from './AddressInput';
import CommonContext, { ApiRoutes } from '../Common';
import {
    onReactSelectChanged,
    FormCheckbox,
    FormLabel
} from '../common/forms/FormElements';
import SlideForm from '../common/forms/SlideForm';
import { Address } from './Address';
import { util } from '../Util';
import { CountyCaptureType } from '../tenant/Tenant';
import GoogleMapReact from 'google-map-react';

export default class AddressFormNew extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();
        this.addRef = React.createRef();
        this.state = {
            address: new Address(),
            formTitle: 'New Address',
            formValidated: false,
            loading: true,
            mapHidden: true,
            errors: {},
            validationMessage: '',
            show: false,
            showSameAs: false,
            showSameAsTitle: '',
            usStateObjects: [],
            usCounties: [],
            isCountyCaptureEnabled: false,
            saveDisabled: false,
            clickedLocation: null
        };
        this.onSubmit = this.onSubmit.bind(this);
        this.onClose = this.onClose.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onSelectChange = this.onSelectChange.bind(this);
        this.onUsStateChange = this.onUsStateChange.bind(this);

        this.debouncedMap = debounce(async () => {
            this.updateMap();
        }, 1000);
    }

    componentDidMount() { return this.populateState(); }

    onChange = async function (e) {
        if (!e || !e.target || !e.target.name) {
            return;
        }

        const changedPropertyName = e.target.name.split('.').pop();

        const { address } = this.state;

        address[changedPropertyName] = e.target.value;

        if (this.isCompleteEnoughToGeocode(address) && ['address1', 'city', 'state'].includes(changedPropertyName)
        ) {
            this.setState({ address, saveDisabled: false });
            this.debouncedMap();
        } else {
            this.setState({ address });
        }
    };

    onCheckedChanged = (e) => {
        const { address } = this.state;
        address[e.target.name] = e.target.checked;
        this.setState({ address });
    };

    onGPSCheckedChanged = (e) => {
        const { address } = this.state;

        address.gpsOnly = e.target.checked;

        if (address.gpsOnly) {
            address.address1 = null;
            address.city = null;
            address.state = null;
            address.zip = null;
        }
        else {
            address.locationName = null;
            address.latitude = null;
            address.longitude = null;
        }

        this.setState({ address });
    };

    onClose = () => {
        this.resetForm();
        this.context.setFormOpened(false);
        this.setState({ show: false });
    };

    onSelectChange = onReactSelectChanged;

    onSubmit = async (e) => {
        this.props.onSaveCallback({ ...this.state.address });
        this.onClose();
    };

    onUsStateChange = async function (item) {
        const stateAbbreviation = (item ?? {}).value ?? '';

        const { address } = this.state;

        address.state = stateAbbreviation;

        if (this.isCompleteEnoughToGeocode(address)) {
            this.setState({ address, saveDisabled: false });
            this.debouncedMap();
        } else {
            this.setState({ address });
        }
    };

    getIndicatedCountyId = (mapResponse) => {
        if (
            !mapResponse
            || mapResponse.status !== 'OK'
            || !mapResponse.results
            || !mapResponse.results.length > 0
        ) {
            return null;
        }

        const { address, usCounties, usStateObjects } = this.state;

        const activeState = usStateObjects.find(
            (s) => s.abbreviation == address.state,
        );

        if (!activeState) {
            return null;
        }

        const activeCounties = usCounties.filter(
            (c) => c.usStateId == activeState.id,
        );

        const responseCounty = mapResponse.results[0].address_components.find(
            (ac) => ac.types.includes('administrative_area_level_2'),
        );

        if (!responseCounty) {
            return null;
        }

        const activeCounty = activeCounties.find(
            (c) => c.name == responseCounty.long_name
                || c.name == responseCounty.short_name,
        );

        if (activeCounty) {
            return activeCounty.id;
        }

        return null;
    };

    isCompleteEnoughToGeocode = (address) => address && address.address1 && address.city && address.state;

    open = (isGlobalAddress, address, title, showSameAs, showSameAsTitle) => {
        this.resetForm();
        const currentAddress = { ...address };
        this.context.setFormOpened(true);
        this.setState(
            {
                address: currentAddress,
                show: true,
                formTitle:
                    title ?? (address.id ? 'Edit Address' : 'New Address'),
                showSameAs,
                showSameAsTitle,
                isGlobalAddress
            },
            () => {
                this.updateMap();

                // callback after its visible
                if (currentAddress.gpsOnly) {

                    var lat = currentAddress.latitude;
                    var lng = currentAddress.longitude;

                    this.setState({
                        clickedLocation: { lat, lng },
                        address: {
                            locationName: currentAddress.locationName,
                            latitude: lat,
                            longitude: lng,
                            gpsOnly: true
                        }
                    });
                }
                else {
                    currentAddress.locationName = null;
                    currentAddress.latitude = null;
                    currentAddress.longitude = null;

                    this.addRef.current.focusAddress();
                }
            },
        );
    };

    populateState = async () => {
        const [usStateObjects, usCounties, tenant] = await Promise.all([
            util.fetch.js(ApiRoutes.USStates()),
            util.fetch.js(ApiRoutes.USCounties.all()),
            util.fetch.js(ApiRoutes.tenant.settings()),
        ]);

        const { tenantSettings } = tenant;
        const countyCaptureEnabled = this.useCountyCapture(tenantSettings);

        this.setState({
            usStateObjects,
            usCounties,
            tenantSettings,
            isCountyCaptureEnabled: countyCaptureEnabled,
            loading: false,
        });
    };

    resetForm = () => this.setState({ formValidated: false });

    updateMap = async () => {
        const { address } = this.state;

        if (!this.isCompleteEnoughToGeocode(address)
        ) {
            this.setState({ saveDisabled: false });
            return;
        }

        const tenantSettings = ((this.context ?? {}).tenant ?? {}).tenantSettings ?? {};
        const apiKey = tenantSettings.googleMapsApiKey;

        const addressPortion = encodeURI(
            `${address.address1} ${address.city},${address.state}`,
        );

        const fetchResponse = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?address=${addressPortion}&key=${apiKey}`,
        );

        const mapResponse = await fetchResponse.json();

        var lat = mapResponse.results[0].geometry.location.lat;
        var lng = mapResponse.results[0].geometry.location.lng;

        address.latitude = lat;
        address.longitude = lng;

        await this.setState({
            clickedLocation: { lat, lng }
        });
    };

    reverseGeocode = async (lat, lng) => {

        const tenantSettings = ((this.context ?? {}).tenant ?? {}).tenantSettings ?? {};
        const apiKey = tenantSettings.googleMapsApiKey;

        const fetchResponse = await fetch(
            'https://maps.googleapis.com/maps/api/geocode/json?latlng=' + lat + ',' + lng + '&location_type=ROOFTOP&key=' + apiKey
        );

        const mapResponse = await fetchResponse.json();
        return mapResponse;
    };

    useCountyCapture = (tenantSettings) => tenantSettings.captureCounty == CountyCaptureType.AllTimesheets
        || (tenantSettings.captureCounty == CountyCaptureType.PrevailingWageTimesheets && this.props.contractUsesPrevailingWage);

    render() {
        const {
            address,
            errors,
            formValidated,
            validationMessage,
            usCounties,
            isCountyCaptureEnabled,
            saveDisabled,
            isGlobalAddress,
            clickedLocation
        } = this.state;
        if (!address) return '';

        var defaultProps = {
            center: {
                lat: clickedLocation?.lat ?? 41.528276,
                lng: clickedLocation?.lng ?? -83.548071
            },
            zoom: 14
        };

        const handleMapClick = async ({ lat, lng }) => {

            let addr = await this.reverseGeocode(lat, lng);

            let address1 = '';
            let city = '';
            let state = '';
            let zip = '';

            if (addr.results.length > 0) {
                for (let i = 0; i < addr.results[0].address_components.length; i++) {
                    const a = addr.results[0].address_components[i];
                    if (a.types[0] == 'street_number') address1 += a.long_name;
                    if (a.types[0] == 'route') address1 += ' ' + a.long_name;
                    if (a.types[0] == 'locality') city = a.long_name;
                    if (a.types[0] == 'administrative_area_level_1') state = a.short_name;
                    if (a.types[0] == 'postal_code') zip = a.long_name;
                }
            }

            this.setState({
                clickedLocation: { lat, lng },
                address: {
                    address1: address1,
                    city: city,
                    state: state,
                    zip: zip,
                    latitude: lat,
                    longitude: lng,
                    gpsOnly: city.length == 0
                }
            });
        };

        const handleMapReady = ({ map, maps }) => {
            // Set custom cursors after the map is ready
            map.setOptions({
                draggableCursor: 'crosshair', // Changes the cursor when the map is draggable
                draggingCursor: 'grabbing',   // Changes the cursor when the map is being dragged
                fullscreenControl: false, // This disables the fullscreen button
                zoomControl: true,
                cameraControl: false,
            });
        };

        return (
            <SlideForm
                loading={this.state.loading}
                show={this.state.show}
                id={this.props.id ?? 'addressForm'}
                formIcon={faAddressCard}
                formTitle={this.state.formTitle}
                ref={this.formRef}
                setIsValidated={(value) => this.setState({ formValidated: value })}
                isValidated={formValidated}
                onSubmit={this.onSubmit}
                onClose={this.onClose}
                onDelete={this.onDelete}
                errors={errors}
                onClearErrors={this.onClearErrors}
                validationMessage={validationMessage}
                saveDisabled={saveDisabled}
            >
                <Row noGutters>
                    <Col>
                        {!address?.gpsOnly &&
                            <AddressInput
                                ref={this.addRef}
                                namePrefix="address"
                                single
                                onChange={this.onChange}
                                onCheckedChanged={this.onCheckedChanged}
                                address={address}
                                isCountyCaptureEnabled={isCountyCaptureEnabled}
                                isGlobalAddress={isGlobalAddress}
                                usCounties={usCounties}
                                onStateChanged={this.onUsStateChange}
                            />
                        }

                        {/*{!isGlobalAddress &&*/}
                        <FormCheckbox
                            small
                            className="ml-3 mt-2"
                            id="gpsOnly"
                            name="GPSOnly"
                            checked={address.gpsOnly}
                            onChange={this.onGPSCheckedChanged}
                            labelText='GPS Only'
                        />
                        {/*}*/}

                        {address.gpsOnly &&
                            <FormGroup>
                                <FormLabel
                                    htmlFor="locationName"
                                    text="Location Name"
                                    required
                                />
                                <Input
                                    id="locationName"
                                    name="locationName"
                                    ref={this.locationNameInput}
                                    onFocus={(e) => {
                                        e.currentTarget.select();
                                    }}
                                    value={address.locationName ?? ''}
                                    onChange={this.onChange}
                                    placeholder="Enter Location name"
                                    type="text"
                                    required
                                />
                                <small className="invalid-feedback text-danger">
                                    Location name is required.
                                </small>
                            </FormGroup>
                        }

                        {!address.gpsOnly && !!this.state.showSameAs && (
                            <FormCheckbox
                                small
                                className="ml-3 mt-2"
                                id="duplicate"
                                name="duplicate"
                                checked={address.duplicate}
                                onChange={this.onCheckedChanged}
                                labelText={this.state.showSameAsTitle ?? ''}
                            />
                        )}

                    </Col>
                </Row>

                <Row>
                    <div style={{ height: '300px', width: '100%' }}>
                        <GoogleMapReact
                            bootstrapURLKeys={{ key: 'AIzaSyBy9gqqNNkL2otuwReKtWw8tBfaueM9lcY' }}
                            defaultCenter={defaultProps.center}
                            center={defaultProps.center}
                            defaultZoom={defaultProps.zoom}
                            onGoogleApiLoaded={handleMapReady}
                            onClick={handleMapClick}
                        >
                            {clickedLocation && (
                                <i lat={clickedLocation.lat} lng={clickedLocation.lng} style={{ fontSize: '14px', color: 'red' }} className='fas fa-circle'></i>
                            )}
                        </GoogleMapReact>
                    </div>
                </Row>

            </SlideForm>
        );
    }
}
